import { cn } from '@/lib/utils';
import { CHART_TYPE_LIST, type ChartTypeId } from '../chart-types/registry';
import FieldLabel from './FieldLabel';
import type { ChartType } from '@/types/report-payload';

interface TypePickerProps {
    /** The current type (any stored value); the gallery highlights it if registered. */
    activeChartType: ChartType;
    disabled?: boolean;
    onApply: (chartType: ChartTypeId) => void;
    /** Hovering (or focusing) a card live-previews that type with the user's data. */
    onHoverChange?: (chartType: ChartTypeId | null) => void;
}

/**
 * "Tipo de gráfico": the chart-type gallery — the wizard's only type picker.
 * One card per chart type (from the registry); picking a card sets the type,
 * everything stays editable afterwards. Hovering a card previews that type on
 * the user's own data without applying anything.
 */
export default function TypePicker({
    activeChartType,
    disabled = false,
    onApply,
    onHoverChange,
}: TypePickerProps) {
    return (
        <div>
            <FieldLabel>Tipo de gráfico</FieldLabel>
            <div className="grid grid-cols-2 gap-1.5">
                {CHART_TYPE_LIST.map((def) => {
                    const active = activeChartType === def.id;
                    return (
                        <button
                            key={def.id}
                            type="button"
                            disabled={disabled}
                            aria-pressed={active}
                            onClick={() => onApply(def.id)}
                            onPointerEnter={() => !disabled && onHoverChange?.(def.id)}
                            onPointerLeave={() => onHoverChange?.(null)}
                            onFocus={() => !disabled && onHoverChange?.(def.id)}
                            onBlur={() => onHoverChange?.(null)}
                            className={cn(
                                'flex flex-col gap-1 rounded-lg border p-2 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                                active
                                    ? 'border-blue-500 bg-blue-50/50'
                                    : 'border-border hover:bg-surface',
                            )}
                        >
                            <def.Thumbnail className="h-10 w-full rounded-md bg-slate-50" />
                            <span className="text-xs font-medium text-foreground">{def.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
